import React, { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { CheckCircle, Home, Mail, Phone, Calendar, Users, Loader2, XCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ThankYou = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [bookingDetails, setBookingDetails] = useState(location.state || {});
  const [error, setError] = useState('');

  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    } else if (location.state) {
      // If we have state from navigation (legacy flow), show success
      setPaymentStatus('paid');
    } else {
      setPaymentStatus('unknown');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      setError('Payment verification timed out. Please check your email for confirmation or contact support.');
      return;
    }

    try {
      const response = await axios.get(`${API}/bookings/checkout-status/${sid}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setPaymentStatus('paid');
        // Fetch booking details
        if (bookingId) {
          try {
            const bookingResponse = await axios.get(`${API}/bookings/${bookingId}`);
            setBookingDetails({
              propertyTitle: bookingResponse.data.property_title,
              checkIn: bookingResponse.data.check_in,
              checkOut: bookingResponse.data.check_out,
              guests: bookingResponse.data.guests,
              nights: bookingResponse.data.nights,
              total: bookingResponse.data.total?.toFixed(2),
              confirmationNumber: bookingResponse.data.id?.slice(0, 8).toUpperCase()
            });
          } catch (err) {
            console.error('Error fetching booking details:', err);
          }
        } else if (data.metadata) {
          setBookingDetails({
            propertyTitle: data.metadata.property_title,
            checkIn: data.metadata.check_in,
            checkOut: data.metadata.check_out,
            guests: parseInt(data.metadata.guests),
            nights: parseInt(data.metadata.nights),
            total: (data.amount_total / 100).toFixed(2),
            confirmationNumber: data.metadata.booking_id?.slice(0, 8).toUpperCase()
          });
        }
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        setError('Payment session has expired. Please try booking again.');
        return;
      }

      // Continue polling
      setPaymentStatus('processing');
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    }
  };

  // Loading state
  if (paymentStatus === 'checking' || paymentStatus === 'processing') {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 flex items-center justify-center" data-testid="thank-you-page">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-white rounded-xl shadow-lg p-12">
            <Loader2 className="h-16 w-16 text-blue-900 animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying Payment</h1>
            <p className="text-gray-600">Please wait while we confirm your payment...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error states
  if (paymentStatus === 'expired' || paymentStatus === 'timeout' || paymentStatus === 'unknown') {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4" data-testid="thank-you-page">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-12 text-center mb-8">
            <div className="mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4">
                <XCircle className="h-12 w-12 text-red-600" />
              </div>
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {paymentStatus === 'expired' ? 'Payment Expired' : 'Payment Status Unknown'}
            </h1>
            <p className="text-gray-600 mb-6">{error || 'We could not verify your payment status.'}</p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/properties"
                className="bg-yellow-600 hover:bg-yellow-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors"
              >
                Browse Properties
              </Link>
              <a
                href="mailto:info@sanchezbnbprestigenet.com"
                className="bg-white hover:bg-gray-50 text-blue-900 py-3 px-6 rounded-lg font-semibold border-2 border-blue-900 transition-colors"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4" data-testid="thank-you-page">
      <div className="max-w-3xl mx-auto">
        {/* Success Card */}
        <div className="bg-white rounded-xl shadow-lg p-12 text-center mb-8">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Thank You!</h1>
          <p className="text-xl text-gray-600 mb-2">Your booking has been confirmed</p>
          <p className="text-gray-500">
            Confirmation number: <span className="font-semibold text-gray-900" data-testid="confirmation-number">
              #{bookingDetails.confirmationNumber || Math.random().toString(36).substr(2, 9).toUpperCase()}
            </span>
          </p>
        </div>

        {/* Booking Details */}
        {bookingDetails.propertyTitle && (
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8" data-testid="booking-details">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Booking Details</h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Home className="h-5 w-5 text-blue-900 mt-1" />
                <div>
                  <p className="text-sm text-gray-600">Property</p>
                  <p className="font-semibold text-gray-900">{bookingDetails.propertyTitle}</p>
                  {bookingDetails.location && (
                    <p className="text-sm text-gray-600">{bookingDetails.location}</p>
                  )}
                </div>
              </div>

              {bookingDetails.checkIn && bookingDetails.checkOut && (
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-blue-900 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Dates</p>
                    <p className="font-semibold text-gray-900">
                      {new Date(bookingDetails.checkIn).toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                      {' - '}
                      {new Date(bookingDetails.checkOut).toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </p>
                    {bookingDetails.nights && (
                      <p className="text-sm text-gray-600">{bookingDetails.nights} nights</p>
                    )}
                  </div>
                </div>
              )}

              {bookingDetails.guests && (
                <div className="flex items-start gap-3">
                  <Users className="h-5 w-5 text-blue-900 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Guests</p>
                    <p className="font-semibold text-gray-900">{bookingDetails.guests} {bookingDetails.guests === 1 ? 'guest' : 'guests'}</p>
                  </div>
                </div>
              )}

              {bookingDetails.total && (
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total Amount Paid</span>
                    <span className="text-2xl font-bold text-gray-900" data-testid="total-amount">${bookingDetails.total}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* What's Next */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">What's Next?</h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-900 text-white rounded-full flex items-center justify-center font-semibold">
                1
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Check Your Email</h3>
                <p className="text-gray-600">We've sent a confirmation email with all your booking details and check-in instructions.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-900 text-white rounded-full flex items-center justify-center font-semibold">
                2
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Host Will Contact You</h3>
                <p className="text-gray-600">Your host will reach out 24-48 hours before check-in with the exact address and access details.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-900 text-white rounded-full flex items-center justify-center font-semibold">
                3
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Enjoy Your Stay!</h3>
                <p className="text-gray-600">Check-in is after 3:00 PM. If you have any questions, contact us anytime.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-blue-900 rounded-xl shadow-lg p-8 text-white mb-8">
          <h2 className="text-2xl font-bold mb-4">Need Help?</h2>
          <p className="mb-6">Our team is here to assist you with any questions about your booking.</p>
          
          <div className="space-y-3">
            <a href="tel:+18006519198" className="flex items-center gap-3 text-white hover:text-yellow-300 transition-colors">
              <Phone className="h-5 w-5" />
              <span className="font-semibold">800-651-9198</span>
            </a>
            <a href="mailto:info@sanchezbnbprestigenet.com" className="flex items-center gap-3 text-white hover:text-yellow-300 transition-colors">
              <Mail className="h-5 w-5" />
              <span className="font-semibold">info@sanchezbnbprestigenet.com</span>
            </a>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Link
            to="/"
            className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white py-4 rounded-lg font-semibold text-center transition-colors"
            data-testid="return-home-btn"
          >
            Return to Home
          </Link>
          <Link
            to="/properties"
            className="flex-1 bg-white hover:bg-gray-50 text-blue-900 py-4 rounded-lg font-semibold text-center border-2 border-blue-900 transition-colors"
            data-testid="browse-properties-btn"
          >
            Browse More Properties
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ThankYou;
